import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { useSubscription } from '@rta/rta-blueprint-library';
import { useContext, useEffect, useState } from 'react';

import { lang } from '@lang';
import { useDesign } from '@hooks';
import { AppContext, DesignActionTypes } from '@context';
import { Feature, FeatureHoookParams } from '..';
import { SaveDirtyKitchenLocally, saveDirtyKitchenLocally } from './helpers';
import { selectNavigation, useAppSelector } from '@state';

type UseAutoSaveFeatureParams = FeatureHoookParams & {
	subscribe?: boolean;
};

// kitchen_is_local IS the dirty flag — subscribing to it lets the flag re-dirty itself, so clearing it on save never sticks
const nonMutationNouns: Array<string> = ['kitchen_is_local'];

export const useAutoSaveFeature = (params: UseAutoSaveFeatureParams = {}): Feature<SaveDirtyKitchenLocally | null> => {
	const { subscribe = true } = params;
	const [controller] = useDesign();

	const { appDispatch } = useContext(AppContext);
	const appNav = useAppSelector(selectNavigation);

	const [value, setValue] = useState<SaveDirtyKitchenLocally | null>(null);

	const autoSaveId = controller?.kitchen?.context?.blueprint?.id ?? '';

	// Only genuine data mutations mark the design dirty — NOT 'loaded' (fires while the view
	// builds/reloads panels, e.g. entering Blueprint View) or view/UI-only events.
	const [updateEvent] = useSubscription(controller?.kitchen ?? null, false, (subscribe
		? [
			['*', 'added'],
			['*', 'deleted'],
			['*', 'updated'],
			['*', 'moved']
		]
		: []
	));

	// save to local storage on every update
	useEffect(() => {
		// console.log('notes:useeffect', { updateEvent, autoSaveId });
		if (updateEvent && !nonMutationNouns.includes(updateEvent.noun)) {
			if (controller && appNav.designId !== null) {
				appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: appNav.designId, isDirty: true }});
				setValue(saveDirtyKitchenLocally(appNav.email, controller as unknown as BpController, false));
			}
		}
	}, [updateEvent]);

	useEffect(() => {
		const savedValue = localStorage.getItem(`autosave-${autoSaveId}`) ?? null;
		if (savedValue) setValue(JSON.parse(savedValue));
	}, []);

	// useEffect(() => {
	// 	const interval = setInterval(() => {
	// 		if (design?.isDirty && !saveFeature.disabled) {
	// 			saveFeature.handler();
	// 			if (appNav.designId !== null) {
	// 				appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: appNav.designId, isDirty: false }});
	// 			}
	// 		}
	// 	}, 10000);
	// 	return () => clearInterval(interval);
	// }, [design?.isDirty]);

	return {
		name: 'autosaveFeature',
		label: lang.customer.label.autoSaveSubmission,
		shortcut: '',
		tooltip: lang.customer.label.autoSaveSubmission,
		icon: () => <></>,
		disabled: false,
		value,
		hidden: false,
		handler: () => {
			localStorage.removeItem(`autosave-${autoSaveId}`);
			setValue(null);
		}
	};
};
